'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import { connect } from 'react-redux';
import emptyFunction from 'react-utils/emptyFunction';
import PropTypes from 'prop-types';
import { bindAll, compact } from 'underscore';
import I18n from 'I18n';
import text from 'I18n/utils/unescapedText';
import { FINDER_FILE_CREATE, FINDER_FOLDER_CREATE, FINDER_THEMES_CREATE, FINDER_FUNCTIONS_CREATE, FINDER_FUNCTIONS_DEPLOY } from 'DesignData/actions/ActionTypes';
import * as FilesActions from 'DesignData/actions/finder/Files';
import * as FoldersActions from 'DesignData/actions/finder/Folders';
import * as FolderSubcategories from 'DesignData/enums/FolderSubcategories';
import { FolderPropTypes } from 'DesignData/propTypes/finder';
import * as finderSelectors from 'DesignData/selectors/finder';
import { EMPTY_ARRAY } from 'DesignData/lib/array';
import NewFolderForm from 'DesignFinderUI/components/forms/NewFolderForm';
import withContextMenuForm from 'DesignFinderUI/components/forms/withContextMenuForm';
import ContextMenuItem from 'DesignData/models/ContextMenuItem';
import ContextMenuItemGroup from 'DesignData/models/ContextMenuItemGroup';
import { getHasServerlessFunctions } from 'DesignData/selectors/auth';

const i18nKey = key => `DesignFinderUI.actions.NewAssetActions.${key}`;

const i18nKeyUpload = key => `DesignFinderUI.actions.UploadAssetActions.${key}`;

const i18nDeployFunctionKey = key => `DesignFinderUI.actions.DeployFunctionActions.${key}`;

const ContextMenuNewFolderForm = withContextMenuForm(NewFolderForm);

function makeMapStateToProps() {
  const getParentFolderProps = finderSelectors.makeGetFolderProps();
  const getFolderPermissions = finderSelectors.makeGetFolderPermissions();
  return (state, ownProps) => {
    const {
      folderId
    } = ownProps;
    const folderPermissions = getFolderPermissions(state, {
      folderId
    });
    const folder = finderSelectors.getFolder(state, {
      folderId
    });
    let parentFolderSubcategory = null;

    if (folder) {
      parentFolderSubcategory = getParentFolderProps(state, {
        folderId: folder.parentFolderId
      }).folderSubcategory;
    }

    const isThemeRootFolder = finderSelectors.getIsThemeRootFolder(state, ownProps);
    const activeThemeFolderId = finderSelectors.getActiveThemeFolderId(state, ownProps);
    const isProjectFolder = finderSelectors.getIsProjectsFolder(state, ownProps);
    const isReadOnlyFolder = !!folder && !!(folder.isReadOnly || folder.isDefault);
    const showCreateFileFolderItems = !isReadOnlyFolder && ( // Only show on theme folders in theme view
    !isThemeRootFolder || !!activeThemeFolderId);
    return {
      canCreateFile: folderPermissions[FINDER_FILE_CREATE],
      canCreateFolder: folderPermissions[FINDER_FOLDER_CREATE],
      canCreateTheme: folderPermissions[FINDER_THEMES_CREATE],
      canCreateFunction: folderPermissions[FINDER_FUNCTIONS_CREATE],
      canDeployFunction: folderPermissions[FINDER_FUNCTIONS_DEPLOY],
      folderName: folder && folder.name,
      folderSubcategory: folder && folder.subcategory,
      parentFolderSubcategory,
      showCreateFileFolderItems,
      isProjectFolder,
      hasServerlessFunctions: getHasServerlessFunctions(state)
    };
  };
}

const mapDispatchToProps = {
  createFolder: FoldersActions.createFolder,
  uploadFiles: FilesActions.uploadFiles,
  validateFileUpload: FilesActions.validateFileUpload
};
export default function withNewAssetContextMenuOptions(WrappedComponent) {
  class HighOrderNewAssetOptions extends Component {
    constructor(props) {
      super(props);

      this.handleNewFunctionClick = e => {
        this.props.onNewFunctionClick(e);
      };

      this.handleUploadFile = e => {
        const {
          folder,
          uploadFiles,
          validateFileUpload
        } = this.props;
        const fileList = Array.from(e.target.files).filter(validateFileUpload);
        uploadFiles(fileList, folder);
      };

      bindAll(this, ['handleDeployFunctionClick', 'handleNewFileClick', 'handleNewFolderCallback', 'handleNewThemeClick', 'handleNewFunctionClick']);
    }

    handleNewFileClick(e) {
      const {
        onNewFileClick
      } = this.props;
      onNewFileClick(e);
    }

    handleNewFolderCallback(data) {
      const {
        createFolder,
        folderId
      } = this.props;

      if (data && data.folderName) {
        createFolder(data.folderName, folderId);
      }
    }

    handleNewThemeClick(e) {
      const {
        onNewThemeClick
      } = this.props;
      onNewThemeClick(e);
    }

    handleDeployFunctionClick(e) {
      const {
        onDeployFunctionClick
      } = this.props;
      onDeployFunctionClick(e);
    }

    createNewFileOption() {
      const {
        canCreateFile
      } = this.props;
      const fileTypeText = text(i18nKey('assets.file'));
      const label = this.createNewOptionText(fileTypeText);
      return new ContextMenuItem({
        label,
        callback: this.handleNewFileClick,
        disabled: !canCreateFile
      });
    }

    createNewFolderOption() {
      const {
        canCreateFolder
      } = this.props;
      const label = this.createNewOptionText(text(i18nKey('assets.folder')));
      return new ContextMenuItem({
        label,
        callback: emptyFunction,
        disabled: !canCreateFolder,
        child: /*#__PURE__*/_jsx(ContextMenuNewFolderForm, {
          callback: this.handleNewFolderCallback
        })
      });
    }

    createNewThemeOption() {
      const fileTypeText = text(i18nKey('assets.theme'));
      const label = this.createNewOptionText(fileTypeText);
      return new ContextMenuItem({
        label,
        callback: this.handleNewThemeClick,
        disabled: !this.props.canCreateTheme
      });
    }

    createNewFunctionOption() {
      const fileTypeText = text(i18nKey('assets.function'));
      const label = this.createNewOptionText(fileTypeText);
      return this.props.hasServerlessFunctions ? new ContextMenuItem({
        label,
        callback: this.handleNewFunctionClick,
        disabled: !this.props.canCreateFunction
      }) : null;
    }

    createUploadFileOption() {
      const fileTypeText = text(i18nKeyUpload('assets.file'));
      const label = this.createNewOptionText(fileTypeText, i18nKeyUpload);
      return new ContextMenuItem({
        label,
        callback: this.handleUploadFile,
        disabled: !this.props.canCreateFile,
        isFileUpload: true,
        divider: true
      });
    }

    createDeployFunctionOption() {
      const {
        canDeployFunction,
        hasServerlessFunctions
      } = this.props;
      const fileTypeText = text(i18nDeployFunctionKey('buttons.default'));
      const label = this.createNewOptionText(fileTypeText, i18nDeployFunctionKey);
      return hasServerlessFunctions && canDeployFunction ? new ContextMenuItem({
        label,
        callback: this.handleDeployFunctionClick
      }) : null;
    }

    createNewOptionText(assetType, customI18nKey = i18nKey) {
      const {
        folderName,
        folderSubcategory
      } = this.props;
      let message = 'default';
      let options = {
        assetType
      };

      if (folderSubcategory !== FolderSubcategories.DESIGN_ROOT) {
        message = 'folder';
        options = Object.assign({}, options, {
          folderName: I18n.SafeString(folderName)
        });
      }

      return text(customI18nKey(`buttons.${message}`), options);
    }

    createContextMenuOptions() {
      if (!this.props.showCreateFileFolderItems || this.props.isProjectFolder) {
        return EMPTY_ARRAY;
      }

      return [new ContextMenuItemGroup({
        options: compact([this.createNewFileOption(), this.createNewFolderOption(), this.createNewThemeOption(), this.createNewFunctionOption(), this.createUploadFileOption(), this.createDeployFunctionOption()])
      })];
    }

    render() {
      return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props, {
        contextMenuOptions: this.createContextMenuOptions()
      }));
    }

  }

  HighOrderNewAssetOptions.propTypes = {
    canCreateFile: PropTypes.bool,
    canCreateFolder: PropTypes.bool,
    canCreateTheme: PropTypes.bool,
    canCreateFunction: PropTypes.bool,
    canDeployFunction: PropTypes.bool,
    createFolder: PropTypes.func.isRequired,
    folder: PropTypes.object,
    folderId: FolderPropTypes.folderId,
    folderName: FolderPropTypes.folderName,
    folderSubcategory: FolderPropTypes.folderSubcategory,
    isProjectFolder: PropTypes.bool,
    onNewFileClick: PropTypes.func,
    onNewThemeClick: PropTypes.func,
    onNewFunctionClick: PropTypes.func,
    onDeployFunctionClick: PropTypes.func,
    parentFolderSubcategory: FolderPropTypes.folderSubcategory,
    showCreateFileFolderItems: PropTypes.bool.isRequired,
    hasServerlessFunctions: PropTypes.bool.isRequired,
    projectsUngated: PropTypes.bool.isRequired,
    uploadFiles: PropTypes.func,
    validateFileUpload: PropTypes.func
  };
  HighOrderNewAssetOptions.defaultProps = {
    canCreateFile: false,
    canCreateFolder: false,
    canCreateTheme: false,
    canCreateFunction: false,
    folderId: null,
    folderName: null,
    folderSubcategory: null,
    onNewFileClick: emptyFunction,
    onNewThemeClick: emptyFunction,
    onNewFunctionClick: emptyFunction,
    onDeployFunctionClick: emptyFunction,
    parentFolderSubcategory: null,
    hasServerlessFunctions: false,
    projectsUngated: false
  };
  return connect(makeMapStateToProps, mapDispatchToProps)(HighOrderNewAssetOptions);
}